import { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  ActionIcon,
} from '@mantine/core'
import { useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { IconAlertCircle, IconRefresh, IconTrash, IconUserPlus, IconX } from '@tabler/icons-react'
import {
  useBatchRemoveOrgMembers,
  useDebounce,
  usePractitioners,
  useRemoveOrgMember,
  type PractitionerRow,
} from '@/hooks'
import { useOrpc } from '@/hooks/useClient'
import { InviteOrgMemberModal } from './InviteOrgMemberModal'
import { MembersTable } from './MembersTable'

type Props = {
  organizationId: string
  organizationName?: string
}

type RoleFilter = 'all' | 'practitioner' | 'care_coordinator'

const ROLE_FILTER_OPTIONS = [
  { value: 'all', label: 'All roles' },
  { value: 'practitioner', label: 'Practitioner' },
  { value: 'care_coordinator', label: 'Care Coordinator' },
]

export function OrganizationMembers({ organizationId, organizationName }: Props) {
  const orpc = useOrpc()
  const queryClient = useQueryClient()

  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebounce(searchInput, 300)
  const [memberToRemove, setMemberToRemove] = useState<PractitionerRow | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [batchModalOpen, setBatchModalOpen] = useState(false)

  const [nameSort, setNameSort] = useState<'asc' | 'desc' | null>(null)
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all')

  const toggleNameSort = () =>
    setNameSort((prev) => (prev === null ? 'desc' : prev === 'desc' ? 'asc' : null))

  // Sort and role filter are applied server-side so they cover the whole org
  // with normal pagination — no need to load every page into memory.
  const {
    practitioners: members,
    total,
    isLoading,
    isFetching,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
  } = usePractitioners(organizationId, {
    search: debouncedSearch,
    role: roleFilter === 'all' ? undefined : roleFilter,
    nameSort: nameSort ?? undefined,
  })

  const remove = useRemoveOrgMember()
  const batchRemove = useBatchRemoveOrgMembers()

  // Selected ids can reference members hidden by the current search or role
  // filter; reset when either changes so we never act on off-screen members.
  useEffect(() => {
    setSelectedIds(new Set())
  }, [debouncedSearch, roleFilter])

  const toggleSelected = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })

  const toggleAll = (checked: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev)
      for (const member of members) {
        if (checked) {
          next.add(member.practitionerId)
        } else {
          next.delete(member.practitionerId)
        }
      }
      return next
    })

  const invalidateMembers = () =>
    queryClient.invalidateQueries({ queryKey: orpc.listPractitioners.key() })

  const handleRemove = async () => {
    if (!memberToRemove) return
    const name = memberToRemove.displayName || 'Member'
    try {
      await remove.mutateAsync({ practitionerId: memberToRemove.practitionerId })
      notifications.show({
        color: 'green',
        title: 'Member removed',
        message: `${name} has been removed from the organization`,
      })
      setMemberToRemove(null)
    } catch {
      // Error notification handled in the mutation hook.
    }
  }

  const handleBatchRemove = async () => {
    const practitionerIds = [...selectedIds]
    if (practitionerIds.length === 0) return
    try {
      const res = await batchRemove.mutateAsync({ practitionerIds })
      if (res.summary.failed === 0) {
        notifications.show({
          color: 'green',
          title: 'Members removed',
          message: `${res.summary.succeeded} member${res.summary.succeeded !== 1 ? 's' : ''} removed from the organization`,
        })
      } else {
        notifications.show({
          color: 'yellow',
          title: 'Some members not removed',
          message: `${res.summary.succeeded} removed, ${res.summary.failed} failed`,
        })
      }
      setSelectedIds(new Set())
      setBatchModalOpen(false)
    } catch {
      // Error notification handled in the mutation hook.
    }
  }

  return (
    <Paper shadow="sm" p="md" withBorder>
      <Stack>
        <Group justify="space-between">
          <Title order={2}>{organizationName || 'Organization'} Members</Title>
          <Group>
            <Button
              variant="subtle"
              leftSection={<IconRefresh size={16} />}
              onClick={() => refetch()}
            >
              Refresh
            </Button>
            <InviteOrgMemberModal
              organizationId={organizationId}
              onInvited={invalidateMembers}
              trigger={<Button leftSection={<IconUserPlus size={16} />}>Invite member</Button>}
            />
          </Group>
        </Group>

        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <TextInput
              placeholder="Search members by name or full email..."
              value={searchInput}
              onChange={(event) => setSearchInput(event.currentTarget.value)}
              w={320}
              rightSection={
                isFetching && !isLoading ? (
                  <Loader size="xs" />
                ) : searchInput ? (
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    aria-label="Clear search"
                    onClick={() => setSearchInput('')}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                ) : null
              }
              rightSectionPointerEvents={searchInput && !isFetching ? 'all' : 'none'}
            />
            <Select
              w={190}
              aria-label="Filter by role"
              data={ROLE_FILTER_OPTIONS}
              value={roleFilter}
              onChange={(value) => setRoleFilter((value as RoleFilter | null) ?? 'all')}
              allowDeselect={false}
            />
          </Group>

          {selectedIds.size > 0 && (
            <Group gap="xs" wrap="nowrap">
              <Text size="sm" fw={500} c="dimmed">
                {selectedIds.size} selected
              </Text>
              <Button variant="subtle" color="gray" onClick={() => setSelectedIds(new Set())}>
                Clear
              </Button>
              <Button
                color="red"
                leftSection={<IconTrash size={16} />}
                onClick={() => setBatchModalOpen(true)}
              >
                Remove selected
              </Button>
            </Group>
          )}
        </Group>

        {isLoading ? (
          <Stack align="center" justify="center" h={480}>
            <Loader size="lg" />
            <Text>Loading organization members...</Text>
          </Stack>
        ) : isError ? (
          <Alert color="red" title="Error loading members" icon={<IconAlertCircle size={16} />}>
            <Stack align="flex-start">
              <Text>Failed to load organization members.</Text>
              <Button
                variant="subtle"
                leftSection={<IconRefresh size={16} />}
                onClick={() => refetch()}
              >
                Try again
              </Button>
            </Stack>
          </Alert>
        ) : (
          <>
            <MembersTable
              organizationId={organizationId}
              members={members}
              hasMore={!!hasNextPage}
              fetchNextPage={fetchNextPage}
              searchActive={!!debouncedSearch.trim()}
              nameSort={nameSort}
              onToggleNameSort={toggleNameSort}
              selectedIds={selectedIds}
              onToggle={toggleSelected}
              onToggleAll={toggleAll}
              onRemove={setMemberToRemove}
              emptyAction={
                <InviteOrgMemberModal
                  organizationId={organizationId}
                  onInvited={invalidateMembers}
                  trigger={
                    <Button leftSection={<IconUserPlus size={16} />} variant="subtle">
                      Invite your first member
                    </Button>
                  }
                />
              }
            />
            <Text size="sm" mx="xs" c="dimmed">
              Showing {members.length} of {total} member{total !== 1 ? 's' : ''}
            </Text>
          </>
        )}
      </Stack>

      <Modal
        opened={memberToRemove !== null}
        onClose={() => setMemberToRemove(null)}
        title="Remove organization member"
        size="lg"
        zIndex={1200}
        centered
      >
        <Stack>
          <Text>
            Are you sure you want to remove <strong>{memberToRemove?.displayName}</strong>?
          </Text>
          <Alert color="yellow" title="Note" icon={<IconAlertCircle size={16} />}>
            This will remove the practitioner from the project, cancel their future visits and
            delete their schedule slots. Their past visits keep the record saved for them, so they
            can still be billed.
          </Alert>
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setMemberToRemove(null)}>
              Cancel
            </Button>
            <Button color="red" loading={remove.isPending} onClick={handleRemove}>
              Remove member
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={batchModalOpen}
        onClose={() => setBatchModalOpen(false)}
        title="Remove organization members"
        size="lg"
        zIndex={1200}
        centered
      >
        <Stack>
          <Text>
            Are you sure you want to remove{' '}
            <strong>
              {selectedIds.size} member{selectedIds.size !== 1 ? 's' : ''}
            </strong>
            ?
          </Text>
          <Alert color="yellow" title="Note" icon={<IconAlertCircle size={16} />}>
            This will remove the selected practitioners from the project, cancel their future visits
            and delete their schedule slots. Their past visits keep the records saved for them, so
            they can still be billed.
          </Alert>
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setBatchModalOpen(false)}>
              Cancel
            </Button>
            <Button color="red" loading={batchRemove.isPending} onClick={handleBatchRemove}>
              Remove members
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  )
}

export default OrganizationMembers
